import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Download, History, ShieldCheck, Lock, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ENTITY_LABELS = {
  expense_category: "Categoria de despesa",
  expense: "Despesa",
  variable_rule: "Regra variável",
  target: "Meta",
  financial_period: "Competência",
  membership: "Usuário",
  organization: "Organização",
  invite: "Convite",
  legal_acceptance: "Termos de uso",
  subscription: "Assinatura",
};

const ACTION_LABELS = {
  create: "Criou",
  create_recurring: "Criou (recorrente)",
  update: "Atualizou",
  delete: "Excluiu",
  deactivate: "Desativou",
  cancel: "Cancelou",
  set_status: "Alterou status",
  set_role: "Alterou papel",
  remove: "Removeu",
  transfer_ownership: "Transferiu propriedade",
  accept_terms: "Aceitou termos",
};

const ACTION_TONE = {
  create: "text-emerald-600 bg-emerald-50",
  create_recurring: "text-emerald-600 bg-emerald-50",
  update: "text-blue-600 bg-blue-50",
  set_status: "text-blue-600 bg-blue-50",
  set_role: "text-blue-600 bg-blue-50",
  delete: "text-red-600 bg-red-50",
  remove: "text-red-600 bg-red-50",
  cancel: "text-red-600 bg-red-50",
  deactivate: "text-amber-600 bg-amber-50",
  transfer_ownership: "text-purple-600 bg-purple-50",
  accept_terms: "text-slate-600 bg-slate-100",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
}

function summarize(obj) {
  if (!obj || typeof obj !== "object") return "";
  return Object.entries(obj)
    .filter(([k]) => !["id", "organization_id", "user_id", "_id"].includes(k))
    .slice(0, 4)
    .map(([k, v]) => `${k}: ${v == null ? "—" : typeof v === "object" ? JSON.stringify(v) : v}`)
    .join(" · ");
}

export default function Auditoria() {
  const [rows, setRows] = useState([]);
  const [entities, setEntities] = useState([]);
  const [actions, setActions] = useState([]);
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("all");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [canExport, setCanExport] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/subscription")
      .then(({ data }) => setCanExport(!!data?.plan?.limits?.data_export))
      .catch(() => setCanExport(true));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (entity !== "all") params.entity = entity;
      if (action !== "all") params.action = action;
      const { data } = await api.get("/audit/logs", { params });
      setRows(data.rows || []);
      setEntities(data.entities || []);
      setActions(data.actions || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entity, action]);

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await api.get("/export/data.zip", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tria_export_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exportação gerada com sucesso");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(false);
    }
  };

  const entityOptions = useMemo(() => entities, [entities]);
  const actionOptions = useMemo(() => actions, [actions]);

  return (
    <div className="space-y-6" data-testid="auditoria-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
            <History className="h-7 w-7 text-accent" /> Dados &amp; Auditoria
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Trilha de auditoria das ações sensíveis e portabilidade dos seus dados.
          </p>
        </div>
        {canExport ? (
          <Button onClick={exportData} disabled={exporting} data-testid="export-data-button">
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Exportar meus dados (.zip)
          </Button>
        ) : (
          <Button variant="outline" onClick={() => navigate("/plano")} data-testid="export-upgrade-button">
            <Lock className="h-4 w-4 mr-2" /> Exportar (disponível no Pro)
          </Button>
        )}
      </div>

      {!canExport && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 flex items-start gap-3" data-testid="export-upgrade-banner">
          <ArrowUpRight className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
          <div className="text-sm text-indigo-900">
            <strong>A exportação de dados faz parte dos planos Pro e Business.</strong> Faça upgrade para baixar todos
            os seus lotes, vendas e despesas em CSV quando quiser.{" "}
            <button onClick={() => navigate("/plano")} className="underline font-medium" data-testid="export-upgrade-link">Ver planos</button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-secondary/40 p-4 flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-accent shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">
          A exportação gera um arquivo <strong>.zip</strong> com todos os seus lotes, mercadorias, vendas, despesas e
          metas em formato CSV (abre no Excel). Seus dados são seus — leve-os quando quiser.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger className="w-[200px]" data-testid="filter-entity"><SelectValue placeholder="Entidade" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as entidades</SelectItem>
            {entityOptions.map((e) => <SelectItem key={e} value={e}>{ENTITY_LABELS[e] || e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-[200px]" data-testid="filter-action"><SelectValue placeholder="Ação" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as ações</SelectItem>
            {actionOptions.map((a) => <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-4 py-3">Data/Hora</th>
                <th className="text-left font-medium px-4 py-3">Usuário</th>
                <th className="text-left font-medium px-4 py-3">Entidade</th>
                <th className="text-left font-medium px-4 py-3">Ação</th>
                <th className="text-left font-medium px-4 py-3">Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-accent" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Nenhum registro de auditoria encontrado.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.id || i} className="border-t border-border hover:bg-secondary/30" data-testid="audit-row">
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtDate(r.timestamp)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.user_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{r.user_email || ""}</div>
                  </td>
                  <td className="px-4 py-3">{ENTITY_LABELS[r.entity] || r.entity}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_TONE[r.action] || "text-slate-600 bg-slate-100"}`}>
                      {ACTION_LABELS[r.action] || r.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground max-w-[380px] truncate" title={summarize(r.after) || summarize(r.before)}>
                    {summarize(r.after) || summarize(r.before) || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
